import {useApiCall, useApiEndpoint} from "@jane/lib/src/api";
import {Employee} from "@jane/lib/src/api/repositories/employees-repository"
import {Card} from "@jane/lib/src/components/Card";
import React, {FC, ReactNode, useMemo} from "react"
import {config} from "../config";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFileCertificate} from "@fortawesome/pro-light-svg-icons";
import {Button} from "../../../lib/src/components/form/Button";
import {LearnCertificate, MyLearnAssignment} from "../../../lib/src/api/repositories/learn-repository";
import {faChevronUp, faGraduationCap} from "@fortawesome/free-solid-svg-icons";
import DataTable from "../../../lib/src/components/data-table/DataTable";

export default function LearnOverview(props: { employee: Employee }): ReactNode {
  const {learn} = useApiCall(config)
  const {resource: certificates} = useApiEndpoint(() => learn.listEmployeeCertificates(props.employee.id), true)
  const {resource: assignments} = useApiEndpoint(() => learn.listEmployeeAssignments(props.employee.id), true)
  const [isCollapsed, setIsCollapsed] = React.useState(true)

  const sortedAssignments = useMemo(() => {
    return assignments?.sort((a,b) => {
      if (a.learn_module?.category?.name === b.learn_module?.category?.name && a.learn_module?.name && b.learn_module?.name) {
        return a.learn_module.name.localeCompare(b.learn_module.name)
      }
      if (a.learn_module?.category?.name && b.learn_module?.category?.name) {
        return a.learn_module.category.name.localeCompare(b.learn_module.category.name)
      }
      if (a.learn_module?.category?.name) {
        return -1
      }
      if (b.learn_module?.category?.name) {
        return 1
      }
      return 0
    }) ?? []
  }, [assignments])

  function downloadCertificate(certificate: LearnCertificate) {
    window.open(certificate.pdf, '_blank')
  }

  return <Card title={'Samen Wijzer (Kennisbank)'}>
    <p className={'text-sm mb-4'}>
      Door het afronden van Kennisbank modules verzamelt een medewerker certificaten.
    </p>
    {isCollapsed ? <>
      <div className={'border border-brand-secondary rounded-md text-center flex flex-col items-center py-8 px-4'}>
        <div className={'text-lg font-medium'}><FontAwesomeIcon icon={faFileCertificate} className={'mr-2'} /> {certificates?.length??0} certificaten</div>
        <div className={'mt-2 mb-4'}>{sortedAssignments.length} toegewezen modules</div>
        <Button type={'primary'} size={'sm'} text={'Bekijk details'} icon={faGraduationCap} onClick={() => setIsCollapsed(false)} />
      </div>
    </> : <>
      <DataTable keyProperty={'id'} placeholder={'Geen certificaten'} data={sortedAssignments} columns={[
        {
          header: 'Naam',
          property: 'module_id',
          transform: (_, assignment) => assignment?.learn_module?.name,
        },
        {
          header: 'Categorie',
          property: 'module_id',
          transform: (_, assignment) => assignment?.learn_module?.category?.name ?? 'n.v.t.',
        },
        {
          header: 'Voortgang',
          property: 'module_id',
          transform: (_, assignment) => <AssignmentProgress assignment={assignment} />,
        },
        {
          header: 'Certificaat',
          property: 'module_id',
          transform: (_, assignment) => <CertificateButton assignment={assignment} certificates={certificates ?? []} />,
        },
      ]} />
      <div className={'mt-4'}>
        <Button type={'primary'} size={'sm'} text={'Details verbergen'} icon={faChevronUp} onClick={() => setIsCollapsed(true)} />
      </div>
    </>}
  </Card>

  // return <Card title={'Certificaten'} id={'certificaten'}>
  //   <p className={'text-sm'}>
  //     Door het afronden van Kennisbank modules verzamelt een medewerker certificaten.
  //   </p>
  //   <div className={'mt-2'}>
  //     {certificates !== undefined ? certificates.map((certificate, i) => <div key={i} className={`flex items-center h-10`}>
  //       <FontAwesomeIcon icon={faFileCertificate}/>
  //       <div className={'mx-3 flex-1 font-medium'}>{certificate.learn_module?.name}</div>
  //       <Button type={'secondary'} size={'xs'} icon={faFileDownload} text={'Download PDF'}
  //               onClick={() => downloadCertificate(certificate)}/>
  //     </div>) : <></>}
  //   </div>
  // </Card>
}

const AssignmentProgress: FC<{assignment: MyLearnAssignment, }> = props => {
  const cardsFinished = useMemo(() => {
    return props.assignment.progress.filter(card => card.finished_at !== null).length
  }, [props.assignment])
  if (cardsFinished === props.assignment.progress.length) {
    return <div className={'text-green-500 font-medium'}>Voltooid</div>
  }
  if (cardsFinished === 0) {
    return <div className={'text-slate-500 font-medium'}>Niet gestart</div>
  }
  <div className={'text-amber-500 font-medium'}>Gestart</div>
}

const CertificateButton: FC<{assignment: MyLearnAssignment, certificates: LearnCertificate[]}> = props => {
  const certificate = useMemo(() => {
    return props.certificates.find(cert => cert.module_id === props.assignment.module_id)
  }, [props.certificates, props.assignment])
  function downloadCertificate(certificate: LearnCertificate) {
    window.open(certificate.pdf, '_blank')
  }
  if (!certificate) {
    return <></>
  }
  return <Button type={'secondary'} size={'xs'} text={'Certificaat'} icon={faGraduationCap} onClick={() => downloadCertificate(certificate)} />
}